import { Banner } from '@ingenico/react-ui/banner';
import { BasicDialogModal } from '@ingenico/react-ui/basic-dialog-modal';
import { CoreAlert } from '@ingenico/react-ui/core-alert';
import { CoreButton } from '@ingenico/react-ui/core-button';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeviceManagementCapability } from '../../hooks/useDeviceManagementCapability.hook';
import { type LogicalDeviceSyncConfigurationModalViewProps } from './LogicalDeviceSyncConfigurationModal.model';
import styles from './LogicalDeviceSyncConfigurationModal.module.scss';
import { getSyncConfigurationWarningContent } from './LogicalDeviceSyncConfigurationModal.util';
import { LogicalDeviceSyncConfigurationModalVariant } from '@ingenico/type-mfe-contracts/domains';

export function LogicalDeviceSyncConfigurationModal({
  isOpen,
  isLoading,
  onSync,
  onCancel,
  variant = LogicalDeviceSyncConfigurationModalVariant.MultipleDevices,
  isError = false,
  errorMessage = '',
  isGroupedDevice = false,
}: LogicalDeviceSyncConfigurationModalViewProps): ReactNode {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.LOGICAL_DEVICE, ALL_LNGS_NAMESPACES.COMMON]);
  const isConfigProfileOrderingEnabled = useDeviceManagementCapability('configProfile.enableConfigProfileOrdering');

  const { titleKey, hasMessage } = getSyncConfigurationWarningContent({
    isConfigProfileOrderingEnabled,
    isGroupedDevice,
    variant,
  });

  const warningMessage = hasMessage
    ? t('syncConfigurationModal.warning.message', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })
    : '';

  return (
    <BasicDialogModal
      actions={
        <div className={styles.modalActions}>
          <CoreButton disabled={isLoading} onClick={onCancel} variant="text">
            {t('button.cancel', { ns: ALL_LNGS_NAMESPACES.COMMON })}
          </CoreButton>
          <CoreButton loading={isLoading} onClick={() => void onSync()} variant="contained">
            {t('button.syncConfiguration', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
          </CoreButton>
        </div>
      }
      customActionsClassName={styles.customActionsClassName}
      customPaperClassName={styles.paperContainer}
      hasCloseIcon
      hasDivider
      isOpen={isOpen}
      onClose={onCancel}
      title={t('syncConfigurationModal.title', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
    >
      <div className={styles.modalContent}>
        {isError && <Banner message={errorMessage} />}
        <CoreAlert
          message={warningMessage}
          severity={'Warning'}
          title={t(titleKey, { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
        />
      </div>
    </BasicDialogModal>
  );
}
